import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { AttioQueryKeys } from 'common/constants/analyst-dashboards';
import { WORKBOARD_PAGE_KEY, WORKBOARD_PAGE_VALUE } from 'common/constants/notes/notes';
import WidgetDrawer from 'components/Drawer/widget-drawer/WidgetDrawer';
import { WidgetDrawerApplicationContext } from 'components/Drawer/widget-drawer/WidgetDrawer.props';
import type { NoteData } from 'components/Notes/types';
import { getAttioNotesForValuation } from 'dashboard409a/services/attio-operation';
import type { AttioNote } from 'dashboard409a/services/attio.types';
import { Logger } from 'utilities';
import { OperationWidgetDrawerProps } from './OperationWidgetDrawer.props';

const OperationWidgetDrawer = ({
  valuationId,
  dealId,
  valuedEntityId,
  primaryContactId,
  isLoading = false,
  isNotesLoading = false,
}: OperationWidgetDrawerProps) => {
  const [notes, setNotes] = useState<NoteData[]>([]);

  const {
    data: notesData,
    isError: isNotesError,
    error: notesError,
    isLoading: isNotesQueryLoading = false,
  } = useQuery<AttioNote[], Error>({
    queryKey: [AttioQueryKeys.NOTES, valuationId, dealId, valuedEntityId, primaryContactId],
    queryFn: () => {
      const records = [
        { parentObject: 'valuations', parentRecordId: valuationId, prefix: 'Valuation' },
      ];

      if (dealId) {
        records.push({ parentObject: 'deals', parentRecordId: dealId, prefix: 'Deal' });
      }

      if (valuedEntityId) {
        records.push({ parentObject: 'companies', parentRecordId: valuedEntityId, prefix: 'Valued Entity' });
      }

      if (primaryContactId) {
        records.push({ parentObject: 'people', parentRecordId: primaryContactId, prefix: 'Primary Contact' });
      }

      return getAttioNotesForValuation(records);
    },
    retry: false,
    refetchOnWindowFocus: false,
    enabled: !!valuationId, // Only run query if valuationId exists
  });

  // Transform Attio notes to our internal NoteData format when notesData changes
  useEffect(() => {
    if (notesData) {
      const transformedNotes: NoteData[] = notesData.map((attioNote, index) => ({
        id: attioNote.id?.note_id ?? index + 1,
        title: attioNote.title ?? '',
        content: attioNote.content_plaintext,
        created_at: attioNote.created_at,
        isNew: false,
        page_type: WORKBOARD_PAGE_VALUE,
      }));
      setNotes(transformedNotes);
    }
  }, [notesData]);

  if (isNotesError) {
    Logger.error('Error fetching Attio notes:', notesError);
  }

  return (
    <WidgetDrawer
      applicationContext={WidgetDrawerApplicationContext.WORKBOARD}
      notesProps={{
        isLoading: isLoading || isNotesLoading || isNotesQueryLoading,
        pageType: WORKBOARD_PAGE_VALUE,
        pageTypeKey: WORKBOARD_PAGE_KEY,
        pageTypeId: valuationId,
        notes,
        setNotes,
        appBarSx: {
          '&.MuiAppBar-positionAbsolute': {
            paddingLeft: '255px',
          },
        },
      }}
      contentWrapperSx={{
        width: 'calc(100% - 255px)',
        marginLeft: '175px',
      }}
      headerContentSx={{
        width: 'calc(100% - 255px)',
        marginLeft: '175px',
      }}
    />
  );
};

export default OperationWidgetDrawer;
